/**
 * WizardEntryNotice.jsx — RC 1.49.6 Wizard Entry Notice (operator-
 * locked, 2026-05-08).
 *
 * Lightweight workflow-guidance dialog rendered immediately before
 * the application wizard opens. NOT an error modal — it surfaces the
 * lifecycle contract (cancel-anytime, in-flight charges may apply,
 * Documents persistence, Interview-Pack dependency on Analyse Fit)
 * so the user enters the wizard with informed expectations.
 *
 * Trigger contract (managed by JobDetailPage):
 *   • User-initiated wizard launch (Apply CTA, sticky mobile CTA,
 *     post-clarifier Continue) → notice fires.
 *   • Auto-reopen on remount via the RC 1.49.4 L-1 marker → notice
 *     does NOT fire (work may already be in flight; surfacing the
 *     notice would be confusing).
 *
 * Hard rails:
 *   • No backend work begins from this surface. Cancel is a true
 *     no-op — wizard does not open and no APIs are touched.
 *   • Continue is the ONLY path that opens the wizard from this
 *     component. Parent handles the actual open via the
 *     `onContinue` callback.
 *   • Operator-locked title + body + button labels — verified by
 *     verbatim source-pin tests.
 */

import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

export function WizardEntryNotice({ open, onContinue, onCancel }) {
  return (
    <Dialog
      open={open}
      onOpenChange={(v) => { if (!v) onCancel(); }}
    >
      <DialogContent
        className="max-w-md"
        data-testid="wizard-entry-notice-dialog"
      >
        <DialogHeader>
          <DialogTitle data-testid="wizard-entry-notice-title">
            Opening your application workspace
          </DialogTitle>
          <DialogDescription className="text-sm leading-relaxed text-foreground space-y-3">
            <span className="block">
              You can leave the workspace at any point.
            </span>
            <span className="block">
              Generated documents are saved to your Documents library
              as soon as they complete, so you can return to them
              from there even after leaving the workspace.
            </span>
            <span className="block">
              The workspace itself is a live session. If you leave
              before assembling the Application Pack, you can start a
              new session later — the Pack can be reassembled from
              your saved documents.
            </span>
            <span className="block">
              You may skip Analyse Fit and still create an
              Application Pack. Interview Prep is only available
              after a completed Analyse Fit tied to that workflow.
            </span>
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 mt-2">
          <Button
            variant="outline"
            onClick={onCancel}
            data-testid="wizard-entry-notice-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={onContinue}
            data-testid="wizard-entry-notice-continue"
          >
            Continue
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default WizardEntryNotice;
